/**
 * @zh 支持 Worker 并行处理的实体系统基类
 * @en Base class for entity systems with Worker parallel processing support
 */
import { EntitySystem } from './EntitySystem';
import { Time } from '../../Utils/Time';
import { PlatformManager } from '../../Platform/PlatformManager';
import { getSystemInstanceTypeName } from '../Decorators';
import { PlatformWorkerPool } from './PlatformWorkerPool';
// =============================================================================
// WorkerEntitySystem
// =============================================================================
/**
 * @zh 支持 Worker 并行处理的 EntitySystem 基类
 * @en Base EntitySystem class with Worker parallel processing support
 *
 * @zh 支持传统 Worker 和 SharedArrayBuffer 两种优化模式：
 * - 传统模式：数据序列化传输，适用于复杂计算
 * - SharedArrayBuffer 模式：零拷贝数据共享，适用于大量简单计算
 *
 * @en Supports two optimization modes:
 * - Traditional mode: Data serialization, for complex calculations
 * - SharedArrayBuffer mode: Zero-copy sharing, for many simple calculations
 *
 * @example
 * ```typescript
 * class PhysicsSystem extends WorkerEntitySystem<PhysicsData> {
 *     constructor() {
 *         super(Matcher.all(Transform, Velocity), {
 *             enableWorker: true,
 *             workerCount: 8,
 *             systemConfig: { gravity: 100 }
 *         });
 *     }
 *
 *     protected getDefaultEntityDataSize(): number {
 *         return 6; // x, y, vx, vy, radius, mass
 *     }
 *
 *     protected extractEntityData(entity: Entity): PhysicsData {
 *         // Extract component data
 *     }
 *
 *     protected workerProcess(entities: PhysicsData[], deltaTime: number, config: unknown): PhysicsData[] {
 *         // Pure function executed in Worker
 *     }
 *
 *     protected applyResult(entity: Entity, result: PhysicsData): void {
 *         // Apply result back to components
 *     }
 * }
 * ```
 */
export class WorkerEntitySystem extends EntitySystem {
    // =========================================================================
    // 构造函数 | Constructor
    // =========================================================================
    constructor(matcher, config = {}) {
        super(matcher);
        this.sharedBuffer = null;
        this.sharedFloatArray = null;
        this.workerPool = null;
        this.isProcessing = false;
        this.hasLoggedSyncMode = false;
        this.platformAdapter = PlatformManager.getInstance().getAdapter();
        this.config = this.resolveConfig(config);
        if (this.config.enableWorker && this.isWorkerSupported()) {
            this.initializeWorkerSystem();
        }
    }
    // =========================================================================
    // 配置解析 | Config Resolution
    // =========================================================================
    /**
     * @zh 解析并验证配置
     * @en Resolve and validate config
     */
    resolveConfig(config) {
        const maxWorkerCount = this.getMaxSystemWorkerCount();
        const requestedWorkerCount = config.workerCount ?? maxWorkerCount;
        const validatedWorkerCount = Math.min(requestedWorkerCount, maxWorkerCount);
        if (requestedWorkerCount > maxWorkerCount) {
            this.logger.warn(`请求 ${requestedWorkerCount} 个 Worker，但系统最多支持 ${maxWorkerCount} 个。` +
                `实际使用 ${validatedWorkerCount} 个 Worker。`);
        }
        return {
            enableWorker: config.enableWorker ?? true,
            workerCount: validatedWorkerCount,
            useSharedArrayBuffer: config.useSharedArrayBuffer ?? this.isSharedArrayBufferSupported(),
            entityDataSize: config.entityDataSize ?? this.getDefaultEntityDataSize(),
            maxEntities: config.maxEntities ?? 10000,
            systemConfig: config.systemConfig,
            ...(config.entitiesPerWorker !== undefined && { entitiesPerWorker: config.entitiesPerWorker }),
            ...(config.workerScriptPath !== undefined && { workerScriptPath: config.workerScriptPath })
        };
    }
    // =========================================================================
    // 平台能力检测 | Platform Capability Detection
    // =========================================================================
    /**
     * @zh 检查是否支持 Worker
     * @en Check Worker support
     */
    isWorkerSupported() {
        return this.platformAdapter.isWorkerSupported();
    }
    /**
     * @zh 检查是否支持 SharedArrayBuffer
     * @en Check SharedArrayBuffer support
     */
    isSharedArrayBufferSupported() {
        return this.platformAdapter.isSharedArrayBufferSupported();
    }
    /**
     * @zh 获取系统支持的最大 Worker 数量
     * @en Get max Worker count supported by system
     */
    getMaxSystemWorkerCount() {
        return this.platformAdapter.getPlatformConfig().maxWorkerCount;
    }
    // =========================================================================
    // 初始化 | Initialization
    // =========================================================================
    /**
     * @zh 初始化 Worker 系统
     * @en Initialize Worker system
     */
    initializeWorkerSystem() {
        if (this.config.useSharedArrayBuffer) {
            this.initializeSharedArrayBuffer();
        }
        this.initializeWorkerPool();
    }
    /**
     * @zh 初始化 SharedArrayBuffer
     * @en Initialize SharedArrayBuffer
     */
    initializeSharedArrayBuffer() {
        try {
            if (!this.isSharedArrayBufferSupported()) {
                this.fallbackToSingleWorker('平台不支持 SharedArrayBuffer');
                return;
            }
            const bufferSize = this.config.maxEntities * this.config.entityDataSize * 4;
            this.sharedBuffer = this.platformAdapter.createSharedArrayBuffer(bufferSize);
            if (this.sharedBuffer) {
                this.sharedFloatArray = new Float32Array(this.sharedBuffer);
                this.logger.info(`${this.systemName}: SharedArrayBuffer 初始化成功 (${bufferSize} bytes)`);
            }
        }
        catch (error) {
            this.fallbackToSingleWorker('SharedArrayBuffer 初始化失败');
            this.logger.warn(`${this.systemName}:`, error);
        }
    }
    /**
     * @zh 降级到单 Worker 模式
     * @en Fallback to single Worker mode
     */
    fallbackToSingleWorker(reason) {
        this.logger.warn(`${this.systemName}: ${reason}，降级到单 Worker 模式`);
        this.config.useSharedArrayBuffer = false;
        this.config.workerCount = 1;
        this.sharedBuffer = null;
        this.sharedFloatArray = null;
    }
    /**
     * @zh 初始化 Worker 池
     * @en Initialize Worker pool
     */
    initializeWorkerPool() {
        try {
            const scriptOrPath = this.resolveWorkerScript();
            if (!scriptOrPath) {
                this.config.enableWorker = false;
                return;
            }
            const workers = this.createWorkers(scriptOrPath);
            this.workerPool = new PlatformWorkerPool(workers, this.sharedBuffer);
        }
        catch (error) {
            this.logger.error(`${this.systemName}: Worker 池初始化失败`, error);
            this.config.enableWorker = false;
        }
    }
    /**
     * @zh 解析 Worker 脚本
     * @en Resolve Worker script
     */
    resolveWorkerScript() {
        const platformConfig = this.platformAdapter.getPlatformConfig();
        // External script path (WeChat Mini Game, etc.)
        if (this.config.workerScriptPath) {
            this.logger.info(`${this.systemName}: 使用外部 Worker 文件: ${this.config.workerScriptPath}`);
            return this.config.workerScriptPath;
        }
        // Platform doesn't support dynamic scripts
        if (platformConfig.limitations?.noEval) {
            this.logger.error(`${this.systemName}: 当前平台不支持动态 Worker 脚本，` +
                `请配置 workerScriptPath 指定预编译的 Worker 文件`);
            return null;
        }
        // Dynamic script (browsers, etc.)
        const script = this.createWorkerScript();
        return (platformConfig.workerScriptPrefix || '') + script;
    }
    /**
     * @zh 创建 Worker 实例数组
     * @en Create Worker instance array
     */
    createWorkers(scriptOrPath) {
        const workers = [];
        for (let i = 0; i < this.config.workerCount; i++) {
            const worker = this.platformAdapter.createWorker(scriptOrPath, {
                name: `${this.systemName}-Worker-${i}`
            });
            workers.push(worker);
        }
        return workers;
    }
    /**
     * @zh 创建 Worker 脚本
     * @en Create Worker script
     */
    createWorkerScript() {
        const functionBody = this.extractFunctionBody(this.workerProcess.toString());
        const sharedProcessBody = this.getSharedProcessFunctionBody();
        const entityDataSize = this.config.entityDataSize;
        return `
            let sharedFloatArray = null;
            const ENTITY_DATA_SIZE = ${entityDataSize};

            self.onmessage = function(e) {
                const { type, id, entities, deltaTime, systemConfig, startIndex, endIndex, sharedBuffer } = e.data;

                try {
                    if (type === 'init' && sharedBuffer) {
                        sharedFloatArray = new Float32Array(sharedBuffer);
                        self.postMessage({ type: 'init', success: true });
                        return;
                    }

                    if (type === 'shared') {
                        if (!sharedFloatArray) {
                            self.postMessage({ id, error: 'SharedArrayBuffer not initialized' });
                            return;
                        }
                        processSharedArrayBuffer(startIndex, endIndex, deltaTime, systemConfig);
                        self.postMessage({ id, result: null });
                        return;
                    }

                    if (entities) {
                        function workerProcess(entities, deltaTime, systemConfig) {
                            ${functionBody}
                        }

                        const result = workerProcess(entities, deltaTime, systemConfig);

                        if (result && typeof result.then === 'function') {
                            result.then(finalResult => {
                                self.postMessage({ id, result: finalResult });
                            }).catch(error => {
                                self.postMessage({ id, error: error.message });
                            });
                        } else {
                            self.postMessage({ id, result });
                        }
                    }
                } catch (error) {
                    self.postMessage({ id, error: error.message });
                }
            };

            function processSharedArrayBuffer(startIndex, endIndex, deltaTime, systemConfig) {
                if (!sharedFloatArray) return;
                ${sharedProcessBody}
            }
        `;
    }
    /**
     * @zh 提取函数体
     * @en Extract function body
     */
    extractFunctionBody(methodStr) {
        const match = methodStr.match(/\{([\s\S]*)\}/);
        if (!match || match[1] === undefined) {
            throw new Error('无法解析 workerProcess 方法');
        }
        return match[1];
    }
    /**
     * @zh 获取 SharedArrayBuffer 处理函数体
     * @en Get SharedArrayBuffer process function body
     */
    getSharedProcessFunctionBody() {
        const processFunc = this.getSharedArrayBufferProcessFunction?.();
        if (!processFunc)
            return '';
        const body = this.extractFunctionBody(processFunc.toString());
        return `
            const userProcessFunction = function(sharedFloatArray, startIndex, endIndex, deltaTime, systemConfig) {
                ${body}
            };
            userProcessFunction(sharedFloatArray, startIndex, endIndex, deltaTime, systemConfig);
        `;
    }
    // =========================================================================
    // 处理逻辑 | Processing Logic
    // =========================================================================
    /**
     * @zh 重写 process 方法，支持 Worker 并行处理
     * @en Override process method with Worker parallel processing
     */
    process(entities) {
        if (this.isProcessing)
            return;
        this.isProcessing = true;
        const mode = this.getCurrentProcessingMode();
        try {
            switch (mode) {
                case 'shared-buffer':
                    this.processWithSharedArrayBuffer(entities).finally(() => {
                        this.isProcessing = false;
                    });
                    break;
                case 'worker':
                    this.processWithWorker(entities).finally(() => {
                        this.isProcessing = false;
                    });
                    break;
                case 'sync':
                default:
                    this.processSynchronously(entities);
                    this.isProcessing = false;
                    break;
            }
        }
        catch (error) {
            this.isProcessing = false;
            this.logger.error(`${this.systemName}: 处理失败`, error);
            throw error;
        }
    }
    /**
     * @zh 获取当前处理模式
     * @en Get current processing mode
     */
    getCurrentProcessingMode() {
        if (!this.config.enableWorker || !this.workerPool) {
            if (!this.hasLoggedSyncMode) {
                this.logger.info(`${this.systemName}: Worker 不可用，使用同步处理`);
                this.hasLoggedSyncMode = true;
            }
            return 'sync';
        }
        if (this.config.useSharedArrayBuffer && this.sharedFloatArray) {
            return 'shared-buffer';
        }
        return 'worker';
    }
    /**
     * @zh 使用 SharedArrayBuffer 优化的 Worker 处理
     * @en Worker processing with SharedArrayBuffer optimization
     */
    async processWithSharedArrayBuffer(entities) {
        if (!this.sharedFloatArray || !this.workerPool)
            return;
        this.writeEntitiesToBuffer(entities);
        const tasks = this.createBatchTasks(entities.length, true);
        await Promise.all(tasks);
        this.readResultsFromBuffer(entities);
    }
    /**
     * @zh 使用 Worker 并行处理
     * @en Worker parallel processing
     */
    async processWithWorker(entities) {
        if (!this.workerPool)
            return;
        const entityData = entities.map(entity => this.extractEntityData(entity));
        const batches = this.createDataBatches(entityData);
        const deltaTime = Time.deltaTime;
        const results = await Promise.all(batches.map(batch => this.workerPool.execute({
            entities: batch,
            deltaTime,
            systemConfig: this.config.systemConfig
        })));
        this.applyBatchResults(entities, results);
    }
    /**
     * @zh 同步处理（fallback）
     * @en Synchronous processing (fallback)
     */
    processSynchronously(entities) {
        const entityData = entities.map(entity => this.extractEntityData(entity));
        const deltaTime = Time.deltaTime;
        const results = this.workerProcess(entityData, deltaTime, this.config.systemConfig);
        if (results && typeof results.then === 'function') {
            results.then(finalResults => {
                this.applyResults(entities, finalResults);
            });
        }
        else {
            this.applyResults(entities, results);
        }
    }
    // =========================================================================
    // 批次处理 | Batch Processing
    // =========================================================================
    /**
     * @zh 创建数据批次
     * @en Create data batches
     */
    createDataBatches(data) {
        return this.splitIntoBatches(data.length, (start, end) => data.slice(start, end));
    }
    /**
     * @zh 创建批次任务
     * @en Create batch tasks
     */
    createBatchTasks(entityCount, useSharedBuffer) {
        return this.splitIntoBatches(entityCount, (startIndex, endIndex) => {
            if (useSharedBuffer) {
                return this.workerPool.executeSharedBuffer({
                    startIndex,
                    endIndex,
                    deltaTime: Time.deltaTime,
                    systemConfig: this.config.systemConfig
                });
            }
            return Promise.resolve();
        });
    }
    /**
     * @zh 通用批次分割逻辑
     * @en Generic batch splitting logic
     */
    splitIntoBatches(totalCount, createBatch) {
        const batches = [];
        const { workerCount, entitiesPerWorker } = this.config;
        if (entitiesPerWorker) {
            for (let i = 0; i < totalCount; i += entitiesPerWorker) {
                const end = Math.min(i + entitiesPerWorker, totalCount);
                batches.push(createBatch(i, end));
            }
        }
        else {
            const batchSize = Math.ceil(totalCount / workerCount);
            for (let i = 0; i < workerCount; i++) {
                const start = i * batchSize;
                const end = Math.min(start + batchSize, totalCount);
                if (start < totalCount) {
                    batches.push(createBatch(start, end));
                }
            }
        }
        return batches;
    }
    // =========================================================================
    // 结果应用 | Result Application
    // =========================================================================
    /**
     * @zh 应用批次结果
     * @en Apply batch results
     */
    applyBatchResults(entities, batchResults) {
        let entityIndex = 0;
        for (const batchResult of batchResults) {
            for (const result of batchResult) {
                if (entityIndex < entities.length && result) {
                    this.applyResult(entities[entityIndex], result);
                }
                entityIndex++;
            }
        }
    }
    /**
     * @zh 应用结果数组
     * @en Apply results array
     */
    applyResults(entities, results) {
        for (let i = 0; i < entities.length && i < results.length; i++) {
            this.applyResult(entities[i], results[i]);
        }
    }
    // =========================================================================
    // SharedArrayBuffer 操作 | SharedArrayBuffer Operations
    // =========================================================================
    /**
     * @zh 将实体数据写入 SharedArrayBuffer
     * @en Write entity data to SharedArrayBuffer
     */
    writeEntitiesToBuffer(entities) {
        if (!this.sharedFloatArray)
            return;
        const count = Math.min(entities.length, this.config.maxEntities);
        for (let i = 0; i < count; i++) {
            const data = this.extractEntityData(entities[i]);
            const offset = i * this.config.entityDataSize;
            this.writeEntityToBuffer(data, offset);
        }
    }
    /**
     * @zh 从 SharedArrayBuffer 读取结果并应用
     * @en Read results from SharedArrayBuffer and apply
     */
    readResultsFromBuffer(entities) {
        if (!this.sharedFloatArray)
            return;
        const count = Math.min(entities.length, this.config.maxEntities);
        for (let i = 0; i < count; i++) {
            const offset = i * this.config.entityDataSize;
            const result = this.readEntityFromBuffer(offset);
            if (result) {
                this.applyResult(entities[i], result);
            }
        }
    }
    // =========================================================================
    // 配置更新 | Config Update
    // =========================================================================
    /**
     * @zh 更新 Worker 配置
     * @en Update Worker config
     */
    updateConfig(newConfig) {
        const oldConfig = { ...this.config };
        if (newConfig.workerCount !== undefined) {
            const maxCount = this.getMaxSystemWorkerCount();
            const validated = Math.min(newConfig.workerCount, maxCount);
            if (newConfig.workerCount > maxCount) {
                this.logger.warn(`请求 ${newConfig.workerCount} 个 Worker，但系统最多支持 ${maxCount} 个。` +
                    `实际使用 ${validated} 个 Worker。`);
            }
            newConfig.workerCount = validated;
        }
        Object.assign(this.config, newConfig);
        if (oldConfig.useSharedArrayBuffer !== this.config.useSharedArrayBuffer) {
            this.reinitializeSystem();
        }
        else if (oldConfig.workerCount !== this.config.workerCount) {
            this.reinitializeWorkerPool();
        }
        else if (!this.config.enableWorker && this.workerPool) {
            this.destroyWorkerPool();
        }
        else if (this.config.enableWorker && !this.workerPool && this.isWorkerSupported()) {
            this.initializeWorkerPool();
        }
    }
    /**
     * @zh 重新初始化整个系统
     * @en Reinitialize entire system
     */
    reinitializeSystem() {
        this.destroyWorkerPool();
        this.sharedBuffer = null;
        this.sharedFloatArray = null;
        if (!this.config.useSharedArrayBuffer) {
            this.config.workerCount = 1;
        }
        if (this.config.enableWorker && this.isWorkerSupported()) {
            this.initializeWorkerSystem();
        }
    }
    /**
     * @zh 重新初始化 Worker 池
     * @en Reinitialize Worker pool
     */
    reinitializeWorkerPool() {
        this.destroyWorkerPool();
        if (this.config.enableWorker && this.isWorkerSupported()) {
            this.initializeWorkerPool();
        }
    }
    /**
     * @zh 销毁 Worker 池
     * @en Destroy Worker pool
     */
    destroyWorkerPool() {
        if (this.workerPool) {
            this.workerPool.destroy();
            this.workerPool = null;
        }
    }
    // =========================================================================
    // 公共 API | Public API
    // =========================================================================
    /**
     * @zh 获取 Worker 系统信息
     * @en Get Worker system info
     */
    getWorkerInfo() {
        return {
            enabled: this.config.enableWorker,
            workerCount: this.config.workerCount,
            ...(this.config.entitiesPerWorker !== undefined && { entitiesPerWorker: this.config.entitiesPerWorker }),
            maxSystemWorkerCount: this.getMaxSystemWorkerCount(),
            isProcessing: this.isProcessing,
            sharedArrayBufferSupported: this.isSharedArrayBufferSupported(),
            sharedArrayBufferEnabled: this.config.useSharedArrayBuffer,
            currentMode: this.getCurrentProcessingMode()
        };
    }
    // =========================================================================
    // 生命周期 | Lifecycle
    // =========================================================================
    /**
     * @zh 销毁系统时清理资源
     * @en Clean up resources on destroy
     */
    onDestroy() {
        super.onDestroy();
        this.destroyWorkerPool();
    }
    getLoggerName() {
        return getSystemInstanceTypeName(this);
    }
}
//# sourceMappingURL=WorkerEntitySystem.js.map